import { Component, inject, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatTabsModule } from '@angular/material/tabs';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { User } from '../../../models/user';

@Component({
  selector: 'app-reminders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatRadioModule,
    MatSelectModule,
    MatFormFieldModule,
    MatListModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatDialogModule,
    DragDropModule,
    MatTabsModule,
    MatSnackBarModule,
    ClipboardModule,
    CdkScrollableModule
  ],
  templateUrl: './reminders.component.html',
  styleUrls: ['./reminders.component.scss']
})
export class RemindersComponent implements OnInit {
  readonly locale = inject(LOCALE_ID);
  population: 'filter' | 'direct' = 'filter';

  // Mocked user sets — replace with real data injection when wiring up
  filterResultsUsers: User[] = [];
  directReportsUsers: User[] = [];

  selectedUserIds = new Set<string>();

  constructor(
    private dialogRef: MatDialogRef<RemindersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { filterPopulation: User[], reportsPopulation: User[], startDate: Date, endDate: Date, user?: User },
    private clipboard: Clipboard,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.filterResultsUsers = this.data.filterPopulation;
    this.directReportsUsers = this.data.reportsPopulation;
    // default select all in the initial population
    this.displayedUsers.forEach(u => this.selectedUserIds.add(u.hawkId));
  }

  get displayedUsers(): User[] {
    return this.population === 'filter' ? this.filterResultsUsers : this.directReportsUsers;
  }

  get allDisplayedSelected(): boolean {
    const users = this.displayedUsers;
    return users.length > 0 && users.every(u => this.isSelected(u));
  }

  onTabChange(index: number): void {
    this.population = index === 0 ? 'filter' : 'direct';
    this.selectedUserIds.clear();
    this.displayedUsers.forEach(u => this.selectedUserIds.add(u.hawkId));
    // ensure selection is consistent when switching populations
    // (optionally auto-select shown users)
    // this.displayedUsers.forEach(u => this.selectedUserIds.add(u.hawkId));
  }

  copyToClipboard(): void {
    const users = this.displayedUsers.filter(u => this.selectedUserIds.has(u.hawkId));
    const text = users.map(u => u.email ?? u.hawkId ?? u.displayName).join('\n');
    if (!text) return;
    try {
      // prefer Angular CDK clipboard when available
      if (this.clipboard) {
        this.clipboard.copy(text);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      }
      this.snackBar.open(`${users.length} addresses copied to clipboard`, 'Close', { duration: 3000 });
    } catch (e) {
      // ignore clipboard errors for now
      console.error('copyToClipboard failed', e);
    }
  }

  toggleUser(user: User): void {
    if (this.selectedUserIds.has(user.hawkId)) this.selectedUserIds.delete(user.hawkId);
    else this.selectedUserIds.add(user.hawkId);
  }

  isSelected(user: User): boolean {
    return this.selectedUserIds.has(user.hawkId);
  }

  selectAll(checked: boolean): void {
    if (checked) this.displayedUsers.forEach(u => this.selectedUserIds.add(u.hawkId));
    else this.displayedUsers.forEach(u => this.selectedUserIds.delete(u.hawkId));
  }

  cancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  openAsSystemMail(): void {
    const people = this.displayedUsers
      .filter(d => this.selectedUserIds.has(d.hawkId) && !!d.email?.trim()) // only include selected users with valid email
      .reduce((acc, cur) => { if (acc.filter(a => a.email == cur.email).length == 0) acc.push(cur); return acc; }, <User[]>[]); // select distinct by email

    const mails = people.map(u => u.email ?? u.hawkId ?? u.displayName); // fallback to hawkId or displayName if email is missing so the user at least sees who is included in the mailto link
    const body = this.applyTemplateVariables(this.reminderBodyDefaults[this.whichTemplate], people);
    const subject = this.applyTemplateVariables(this.reminderSubjectDefaults[this.whichTemplate], people);
    const link = `mailto:${mails.map(m => encodeURIComponent(m)).join(";")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = link;
    this.snackBar.open(`Opening ${this.selectedUserIds.size} users in system mail`, 'Close', { duration: 3000 });
  }

  whichTemplate: "other" | "review" | "unsubmitted" = "other";
  reminderSubjectDefaults = {
    "other": "Timesheets between {{start}} and {{end}}",
    "review": "Please review your timesheets",
    "unsubmitted": "You have unsubmitted timesheets"
  };
  reminderBodyDefaults = {
    "other": "{{nameSummary}},\n\nRegarding your timesheets between {{start}} and {{end}}\n\n\n- {{self}}\n",
    "review": "{{nameSummary}},\n\nI am returning some timesheets for review between {{start}} and {{end}}. Please look them over and correct any indicated times.\n\nThanks,\n{{self}}\n",
    "unsubmitted": "{{nameSummary}},\n\nYou have unsubmitted timesheets between {{start}} and {{end}}. Please submit them at your earliest convenience. \n\nThanks,\n{{self}}\n",
  };

  applyTemplateVariables(tmpl: string, users: User[]) {
    const vars: { [key: string]: string } = {
      nameSummary: users.length < 4 ? users.map(u => u.first).join(', ') : "Everyone",
      start: formatDate(this.data.startDate, "yyyy-MM-dd", this.locale),
      end: formatDate(this.data.endDate, "yyyy-MM-dd", this.locale),
      self: this.data.user?.first ?? 'Management'
    };
    for (let key in vars) {
      tmpl = tmpl.replaceAll(`{{${key}}}`, vars[key]);
    }
    return tmpl;
  }
}
